/* eslint-disable no-alert */
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ColoredBar from '../../components/ColoredBar/ColoredBar';
import Nav from '../../components/Nav/Nav';
import './managepermissions.css';
import MetricsContext from '../../context/MetricsContext';
import PageNames from '../../utils/PageNames';
import PageTypes from '../../utils/PageTypes';
import {
  getGeolocationPermissionState,
  getUserLocation,
  persistUserLocation,
  requestCurrentLocation,
} from '../../utils';
import { acceptCookieConsent, COOKIE_CONSENT_TYPES, rejectCookieConsent } from '../../utils/cookieConsent';
import { ensureNotificationSubscription } from '../../utils/notifications';

export default function ManagePermissions() {
  const { t } = useTranslation();
  const { updateUserMetric } = useContext(MetricsContext);

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [cookiesEnabled, setCookiesEnabled] = useState(false);

  const sessionId = localStorage.getItem('sessionId');
  const cookies = localStorage.getItem('cookieConsent');

  useEffect(() => {
    updateUserMetric({
      name: PageNames.MANAGE_PERMISSIONS,
      pageType: PageTypes.DEFAULT,
    });

    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    } else {
      setNotificationsEnabled(false);
    }

    getGeolocationPermissionState()
      .then((permissionState) => {
        if (permissionState === 'granted') {
          setLocationEnabled(true);
          return;
        }

        if (permissionState === 'unknown') {
          setLocationEnabled(Boolean(getUserLocation()));
          return;
        }

        setLocationEnabled(false);
      })
      .catch(() => {
        setLocationEnabled(Boolean(getUserLocation()));
      });

    setCookiesEnabled(cookies === 'accepted');
  }, [cookies, sessionId, updateUserMetric]);

  const handleNotificationToggle = async () => {
    if (!('Notification' in window)) {
      window.alert(t('permissions.alerts.notificationsUnsupported'));
      return;
    }

    if (Notification.permission === 'granted') {
      setNotificationsEnabled(true);
      window.alert(t('permissions.alerts.notificationsAlreadyEnabled'));
      return;
    }

    if (Notification.permission === 'denied') {
      setNotificationsEnabled(false);
      window.alert(t('permissions.alerts.notificationsBlocked'));
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        await ensureNotificationSubscription();
        setNotificationsEnabled(true);
      } else {
        setNotificationsEnabled(false);
      }
    } catch {
      setNotificationsEnabled(false);
      window.alert(t('permissions.alerts.notificationsUpdateFailed'));
    }
  };

  const handleLocationToggle = async () => {
    const permissionState = await getGeolocationPermissionState();

    if (permissionState === 'unsupported') {
      window.alert(t('permissions.alerts.locationUnsupported'));
      return;
    }

    try {
      const hadStoredLocation = Boolean(getUserLocation());

      if (permissionState === 'denied') {
        setLocationEnabled(false);
        window.alert(t('permissions.alerts.locationBlocked'));
        return;
      }

      const position = await requestCurrentLocation();
      await persistUserLocation(position);
      setLocationEnabled(true);

      if (permissionState === 'granted' && hadStoredLocation) {
        window.alert(t('permissions.alerts.locationRefreshed'));
      }
    } catch {
      setLocationEnabled(false);
      window.alert(t('permissions.alerts.locationNotGranted'));
    }
  };

  const handleCookiesToggle = () => {
    if (cookiesEnabled) {
      rejectCookieConsent();
      setCookiesEnabled(false);
    } else {
      acceptCookieConsent(COOKIE_CONSENT_TYPES.ALL);
      setCookiesEnabled(true);
    }

    window.location.reload();
  };

  return (
    <div className="managePermissionsPage">
      <Nav goBack logo={false} label={t('permissions.title')} />
      <ColoredBar />
      <div className="containerContent">
        <ol type="1">
          <li>
            <div className="status">
              <h4 className="permissions__titles">{t('permissions.notifications.title')}</h4>
              <div className="switch__container">
                <input
                  id="notificationToggle"
                  aria-label={t('permissions.notifications.title')}
                  checked={notificationsEnabled}
                  type="checkbox"
                  className="offscreen"
                  onChange={handleNotificationToggle}
                />
                <label htmlFor="notificationToggle" className="switch" />
              </div>
            </div>
            <p>{t('permissions.notifications.description')}</p>
            <ol type="a">
              <li>{t('permissions.notifications.list.item1')}</li>
              <li>{t('permissions.notifications.list.item2')}</li>
              <li>{t('permissions.notifications.list.item3')}</li>
            </ol>
            <p>{t('permissions.notifications.note')}</p>
          </li>
          <br />

          <li>
            <div className="status">
              <h4 className="permissions__titles">{t('permissions.location.title')}</h4>
              <div className="switch__container">
                <input
                  id="locationToggle"
                  aria-label={t('permissions.location.title')}
                  type="checkbox"
                  className="offscreen"
                  checked={locationEnabled}
                  onChange={handleLocationToggle}
                />
                <label htmlFor="locationToggle" className="switch" />
              </div>
            </div>
            <p>{t('permissions.location.description')}</p>
            <ol type="a">
              <li>{t('permissions.location.list.item1')}</li>
              <li>{t('permissions.location.list.item2')}</li>
            </ol>
            <p>{t('permissions.location.note')}</p>
          </li>
          <br />

          <li>
            <div className="status">
              <h4 className="permissions__titles">{t('permissions.cookies.title')}</h4>
              <div className="switch-container">
                <input
                  id="cookiesToggle"
                  aria-label={t('permissions.cookies.title')}
                  type="checkbox"
                  className="offscreen"
                  checked={cookiesEnabled}
                  onChange={handleCookiesToggle}
                />
                <label htmlFor="cookiesToggle" className="switch" />
              </div>
            </div>
            <p>{t('permissions.cookies.description')}</p>
          </li>
        </ol>
      </div>
    </div>
  );
}
